import { useQuery } from '@tanstack/react-query'

import { generateUniqueId, type AppTimezonePreference } from '@sparkai/utils'

import { apiClient } from '../apiClient'
import {
  getOperatorPerformanceQueryTimezonePart,
  getOperatorPerformanceRequestHeaders,
} from './operatorPerformanceTimezone'

type GetProgramErrorsRequest = {
  operatorId?: string
  programIds: string[]
  startDate: string
  endDate: string
  timezonePreference?: AppTimezonePreference
}

type GetProgramErrorsResponse = {
  errors: {
    programId: string
    iconType: string | null
    color: string
    displayName: string
    subclass: string | null
    class: string
    mistakenBy: string
    type: 'response' | 'classification' | 'object_detection'
    count: number
    share: number
  }[]
  classifications: {
    programId: string
    iconType: string | null
    color: string
    displayName: string
    class: string
    count: number
    share: number
    accuracy: number
    annotationType: string
  }[]
}

export const useGetProgramErrors = ({
  operatorId,
  programIds,
  startDate,
  endDate,
  timezonePreference,
}: GetProgramErrorsRequest) => {
  const timezoneQueryKeyPart = getOperatorPerformanceQueryTimezonePart(timezonePreference)

  return useQuery({
    queryKey: [
      'operator-program-errors',
      operatorId,
      startDate,
      endDate,
      programIds,
      ...(timezoneQueryKeyPart ? [timezoneQueryKeyPart] : []),
    ],
    queryFn: async () => {
      const headers = getOperatorPerformanceRequestHeaders(timezonePreference)
      const { data } = await apiClient.get<GetProgramErrorsResponse>(
        `/int/v3/operators/${operatorId}/program-errors`,
        {
          params: { startDate, endDate, programIds: programIds.join() },
          ...(headers ? { headers } : {}),
        }
      )
      return data
    },
    select: (data) => {
      return {
        errors: data.errors.map((e) => ({ ...e, key: generateUniqueId() })),
        classifications: data.classifications.map((c) => ({ ...c, key: generateUniqueId() })),
      }
    },
    enabled: Boolean(operatorId) && programIds.length > 0,
    refetchOnWindowFocus: false,
  })
}
